import React, { useEffect, useMemo, useState } from 'react'

import { ChevronDown } from 'lucide-react'
// import { Menu } from 'lucide-react'
import { useTheme } from 'next-themes'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { useAuth } from '@/core/hooks/useAuth'
import { usePermission } from '@/core/hooks/usePermission'
import { RootState } from '@/core/store'
import { setTheme as setUITheme } from '@/core/store/slices/ui'
import { Button } from '@/shared/components/atoms/Button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	DropdownMenuSeparator,
} from '@/shared/components/atoms/Dropdown/Dropdown'
import Icon from '@/shared/components/atoms/Icon'
import { AccountSelectorModal } from '@/shared/components/organisms/AccountSelectorModal/AccountSelectorModal'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'

import { LogoIcon } from '@shared/components/atoms/Logo'

import { NavbarContent } from '../Navbar/NavBar'

interface TopAppBarProps {
	userName?: string
	middleContent?: React.ReactNode
	leftContent?: React.ReactNode
	className?: string
	showLogo?: boolean
}

const TopAppBar: React.FC<TopAppBarProps> = ({
	userName = '',
	middleContent,
	leftContent,
	showLogo = false,
}) => {
	const { logout } = useAuth()
	const { t } = useTranslation()
	const { hasAccountSelectorAccess } = usePermission()
	const { theme: nextTheme, resolvedTheme, setTheme } = useTheme()
	const dispatch = useDispatch()
	const [isAccountSelectorOpen, setIsAccountSelectorOpen] = useState(false)
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
	const navigate = useNavigate()
	const location = useLocation()
	const { theme: uiTheme } = useSelector((state: RootState) => state.ui)
	const hasCompactMiddleSpacing =
		location.pathname === '/account' || location.pathname === '/integrations'

	const fetchAllDataEnabled = useSelector(
		(state: RootState) => state.fetchAllData.enabled
	)

	const currentTheme: 'light' | 'dark' =
		resolvedTheme === 'dark' || nextTheme === 'dark' ? 'dark' : 'light'

	useEffect(() => {
		if (uiTheme !== currentTheme) {
			dispatch(setUITheme(currentTheme))
		}
	}, [currentTheme, dispatch, uiTheme])

	// (selectedAccount selector removed; account name is derived from currentAccount)
	const canAccessAccountSelector = useMemo(() => {
		return hasAccountSelectorAccess()
	}, [hasAccountSelectorAccess])

	const currentAccount = useCurrentAccount()

	const accountPlan = useMemo(() => {
		const activeSubscription = currentAccount?.getActiveSubscriptions()?.[0]
		const trialSubscription = currentAccount?.getTrialSubscriptions()?.[0]
		return activeSubscription?.plan?.name || trialSubscription?.plan?.name
	}, [currentAccount])

	// Check if the account is in trial
	const trialSubscriptions = currentAccount?.getTrialSubscriptions() || []
	const isInTrial = trialSubscriptions.length > 0
	const trialDaysRemaining = currentAccount?.getRemainingTrialDays()

	// Extract the first name and get the initial
	const getFirstNameInitial = (name: string): string => {
		if (!name) return 'M'
		// Split by space and get the first part as first name
		const firstName = name.split(' ')[0]
		return firstName.charAt(0).toUpperCase()
	}

	const userInitial = getFirstNameInitial(userName)
	const userFirstName = userName ? userName.split(' ')[0] : ''

	const handleLogout = () => {
		logout()
	}

	const handleOpenAccountSelector = () => {
		setIsAccountSelectorOpen(true)
	}

	const handleGoToAccount = () => {
		navigate('/account')
	}

	const handleToggleTheme = () => {
		const nextTheme: 'light' | 'dark' =
			currentTheme === 'light' ? 'dark' : 'light'

		setTheme(nextTheme)
	}

	const toggleThemeLabel = currentTheme === 'light' ? 'Dark' : 'Light'

	return (
		<>
			<header
				className={`bg-card md:bg-transparent relative ${showLogo ? '' : 'lg:pl-20'}`}
			>
				<div className="w-full px-0 md:pl-3 md:pr-3 2xl:pl-6 2xl:pr-6 md:pt-3 2xl:pt-6">
					<div className="max-w-[1400px] mx-auto w-full">
						<div className="h-[84px] md:border md:border-border rounded-xl px-4 2xl:px-8 flex items-center justify-between bg-card">
							{leftContent ? (
								<div className="flex items-center mr-3">{leftContent}</div>
							) : null}
							{/* Hamburger menu - mobile only */}
							{/* <Menu
								className="h-5 w-5 lg:hidden cursor-pointer mr-3"
								onClick={() => setIsMobileMenuOpen(true)}
							/> */}

							{/* Guide button - disabled for now */}
							{/* {isInTrial && (
								<Button
									variant="secondary"
									size="sm"
									className="lg:hidden rounded-full px-3 h-8 text-xs font-medium relative"
									onClick={() => navigate('/guide')}
								>
									{t('navigation.guide')}
									{guideBadgeCount > 0 && (
										<span className="absolute -top-1 -right-1 min-w-4 h-4 flex items-center justify-center px-1 text-[10px] font-semibold bg-primary text-secondary rounded-full">
											{guideBadgeCount > 9 ? '9+' : guideBadgeCount}
										</span>
									)}
								</Button>
							)} */}

							{showLogo && (
								<div className="hidden lg:flex items-center mr-4">
									<LogoIcon width="28px" height="28px" />
								</div>
							)}

							<div
								className={`hidden md:flex items-center ${
									hasCompactMiddleSpacing ? 'md:ml-2 lg:ml-3' : ''
								}`}
							>
								{middleContent}
							</div>

							<div className="flex items-center gap-4 ml-auto">
								{/* Trial Banner - Only shows for trial accounts with remaining days (not expired) */}
								{isInTrial &&
									trialDaysRemaining != null &&
									trialDaysRemaining > 0 && (
										<div className="md:flex items-center gap-3 px-4 py-1.5 rounded-lg lg:bg-main-accent">
											<span className="hidden md:block text-sm font-medium text-primary">
												{trialDaysRemaining === 1
													? t('trial.day_left', { count: trialDaysRemaining })
													: t('trial.days_left', { count: trialDaysRemaining })}
											</span>
											<Button
												size="sm"
												className="bg-primary text-secondary rounded-full px-4 h-7 text-xs font-semibold"
												onClick={() => navigate('/subscribe')}
											>
												{t('trial.subscribe')}
											</Button>
										</div>
									)}

								<div className="md:hidden relative">
									<button
										title={userName}
										className="w-9 h-9 rounded-full bg-background/30 text-foreground flex items-center justify-center font-semibold text-base cursor-pointer transition-colors hover:bg-background/20"
										onClick={() => navigate('/account')}
									>
										{userInitial}
									</button>
								</div>

								{/* Selected Account Indicator (separate pill) */}
								<div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/5 border border-primary/20">
									<div className="w-2 h-2 rounded-full bg-primary" />
									<span
										className="text-sm font-medium text-foreground max-w-[120px] truncate"
										title={currentAccount?.name}
									>
										{currentAccount?.name}
									</span>
									{fetchAllDataEnabled && (
										<span className="inline-flex items-center rounded-full bg-main-accent/20 text-main-accent px-2 py-0.5 text-xs font-medium">
											{t('account_selector.fetch_all_data_active')}
										</span>
									)}
								</div>

								{/* User + plan pill (dropdown trigger) */}
								<div className="hidden md:block">
									<DropdownMenu>
										<DropdownMenuTrigger asChild>
											<button
												type="button"
												className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-card border border-border hover:bg-muted transition-colors"
												title={userName}
											>
												<span className="text-sm font-medium text-foreground truncate max-w-[140px]">
													{userFirstName || userName}
												</span>
												{accountPlan ? (
													<span className="inline-flex items-center rounded-full bg-main-accent/20 text-main-accent px-2 py-0.5 text-xs font-medium">
														{accountPlan}
													</span>
												) : null}
												<ChevronDown className="w-4 h-4 text-muted-foreground" />
											</button>
										</DropdownMenuTrigger>
										<DropdownMenuContent align="end">
											<DropdownMenuItem onClick={handleGoToAccount}>
												<Icon name="person" width="20px" height="20px" />
												{t('navigation.account')}
											</DropdownMenuItem>
											<DropdownMenuSeparator />
											{canAccessAccountSelector && (
												<>
													<DropdownMenuItem onClick={handleOpenAccountSelector}>
														<Icon
															name="arrow-clockwise"
															width="20px"
															height="20px"
														/>
														{t('navigation.switch_account')}
													</DropdownMenuItem>
													<DropdownMenuSeparator />
												</>
											)}
											<DropdownMenuItem onClick={handleToggleTheme}>
												<Icon
													name={
														currentTheme === 'light' ? 'moon-fill' : 'sun-fill'
													}
													width="20px"
													height="20px"
												/>
												{toggleThemeLabel}
											</DropdownMenuItem>
											<DropdownMenuSeparator />
											<DropdownMenuItem onClick={handleLogout}>
												<Icon name="logout" width="20px" height="20px" />
												{t('navigation.logout')}
											</DropdownMenuItem>
										</DropdownMenuContent>
									</DropdownMenu>
								</div>
							</div>
						</div>
					</div>
				</div>
			</header>

			{/* Mobile Menu Overlay */}
			{isMobileMenuOpen && (
				<div className="fixed inset-0 z-50 lg:hidden">
					{/* Backdrop */}
					<div
						className="absolute inset-0 bg-background/80 backdrop-blur-sm"
						onClick={() => setIsMobileMenuOpen(false)}
					/>
					{/* Drawer */}
					<div className="absolute left-0 top-0 bottom-0 w-fit lg:w-[72px] bg-card border-r border-border animate-in slide-in-from-left duration-300 flex flex-col items-center">
						<NavbarContent onItemClick={() => setIsMobileMenuOpen(false)} />
					</div>
				</div>
			)}

			{/* Account Selector Modal */}
			{canAccessAccountSelector && (
				<AccountSelectorModal
					open={isAccountSelectorOpen}
					onOpenChange={setIsAccountSelectorOpen}
				/>
			)}
		</>
	)
}

export default TopAppBar
